"use client";

import { useState } from "react";
import Button, { ButtonType } from "@/app/(components)/generics/Button/Button";
import CTA from "@/app/(components)/specifics/pages/Home/components/CTA/CTA";
import { ModalStore } from "@/app/stores/ModalStore";
import { TKey } from "@/app/translations/TKey";
import { useT } from "@/app/translations/useT";
import { PlanSettings } from "@/app/types/billing/PlanSettings";
import { DefaultProps } from "@/app/types/common/DefaultProps";
import { HomeCtaAction } from "@/app/types/common/HomeCtaAction";
import { HomePricingPeriod } from "@/app/types/home/HomePricing";
import { UPlan } from "@/app/utils/UPlan";
import { UStyles } from "@/app/utils/UStyles";
import homeStyles from "../../Home.module.css";
import {
  HOME_PRICING_CUSTOM_FEATURES,
  HOME_PRICING_PERIODS,
  HOME_PRICING_PRO_FEATURES,
} from "./Pricing.logic";
import styles from "./Pricing.module.css";
import PricingFeatures from "./PricingFeatures";

type Props = DefaultProps & {
  plan: PlanSettings;
};

export default function Pricing(props: Props) {
  const T = useT();
  const { params, plan } = props;

  const [period, setPeriod] = useState<HomePricingPeriod>(HomePricingPeriod.ANNUAL);

  const isAnnual = period === HomePricingPeriod.ANNUAL;
  const monthlyPrice = UPlan.monthlyPriceEur(plan, isAnnual);

  const billingNote = isAnnual
    ? T(TKey.HOME_MEIDLY_PRICING_BILLED_ANNUALLY).replace(
        "{{price}}",
        String(UPlan.annualPriceEur(plan)),
      )
    : T(TKey.HOME_MEIDLY_PRICING_BILLED_MONTHLY);

  return (
    <div className={styles.container}>
      <div className={homeStyles.titles}>
        <h3 className={homeStyles.supTitle}>{T(TKey.HOME_MEIDLY_PRICING_SUP_TITLE)}</h3>
        <h2 className={homeStyles.statement}>{T(TKey.HOME_MEIDLY_PRICING_TITLE)}</h2>
      </div>

      <div className={styles.periods}>
        {HOME_PRICING_PERIODS.map((tab) => (
          <button
            type="button"
            key={tab.period}
            onClick={() => setPeriod(tab.period)}
            className={UStyles.classNames(
              styles.period,
              tab.period === period ? styles.periodActive : "",
            )}
          >
            {T(tab.labelKey)}
            {tab.period === HomePricingPeriod.ANNUAL && (
              <span className={styles.periodSave}>
                {T(TKey.HOME_MEIDLY_PRICING_PERIOD_SAVE).replace(
                  "{{discount}}",
                  String(plan.annualDiscountPercent),
                )}
              </span>
            )}
          </button>
        ))}
      </div>

      <div className={styles.plans}>
        <div className={UStyles.classNames(styles.plan, styles.planPro)}>
          <div className={styles.planHead}>
            <span className={styles.planName}>{T(TKey.HOME_MEIDLY_PRICING_PRO_NAME)}</span>
          </div>

          <p className={styles.price}>
            <span className={styles.priceAmount}>{`${monthlyPrice}€`}</span>
            <span className={styles.priceUnit}>{T(TKey.HOME_MEIDLY_PRICING_PER_MONTH)}</span>
            <span className={styles.priceVat}>{T(TKey.HOME_MEIDLY_PRICING_VAT)}</span>
          </p>

          <p className={styles.billing}>{billingNote}</p>

          <p className={styles.tagline}>{T(TKey.HOME_MEIDLY_PRICING_PRO_TAGLINE)}</p>

          <div className={styles.cta}>
            <CTA
              params={params}
              min={false}
              hideIcon={true}
              action={HomeCtaAction.SIGN_UP}
              ctaKey={TKey.HOME_CTA_START_FREE}
            />
          </div>

          <p className={styles.included}>{T(TKey.HOME_MEIDLY_PRICING_INCLUDED)}</p>

          <PricingFeatures features={HOME_PRICING_PRO_FEATURES} plan={plan} />
        </div>

        <div className={styles.plan}>
          <div className={styles.planHead}>
            <span className={styles.planName}>{T(TKey.HOME_MEIDLY_PRICING_CUSTOM_NAME)}</span>
          </div>

          <p className={styles.price}>
            <span className={styles.priceTalk}>{T(TKey.HOME_MEIDLY_PRICING_CUSTOM_PRICE)}</span>
          </p>

          <p className={styles.billing}>{T(TKey.HOME_MEIDLY_PRICING_CUSTOM_BILLING)}</p>

          <p className={styles.tagline}>{T(TKey.HOME_MEIDLY_PRICING_CUSTOM_TAGLINE)}</p>

          <div className={styles.cta}>
            <Button
              params={params}
              isLink={false}
              type={ButtonType.OUTLINE}
              text={T(TKey.HOME_CTA_BOOK_DEMO)}
              onClick={() => ModalStore.getState().openDemo()}
            />
          </div>

          <p className={styles.included}>{T(TKey.HOME_MEIDLY_PRICING_INCLUDED)}</p>

          <PricingFeatures features={HOME_PRICING_CUSTOM_FEATURES} plan={plan} />
        </div>
      </div>
    </div>
  );
}
